import React from 'react';
import './ScaleGauge.css';

/**
 * ScaleGauge Component
 * Visual progress bar for weight measurement
 * Shows current weight vs target with color coding
 */
function ScaleGauge({
  currentWeight,
  targetWeight,
  isStable = false,
  tolerance = 0.5,
  unit = 'lbs'
}) {
  // Calculate percentage
  const percentage = targetWeight > 0
    ? Math.min((currentWeight / targetWeight) * 100, 120)
    : 0;

  // Calculate remaining
  const remaining = Math.max(0, targetWeight - currentWeight);
  const isOverfilled = currentWeight > targetWeight + tolerance;
  const isWithinTolerance = Math.abs(currentWeight - targetWeight) <= tolerance;
  const isComplete = currentWeight >= targetWeight - tolerance;

  // Determine color based on state
  const getProgressColor = () => {
    if (isOverfilled) return 'overfilled';
    if (isWithinTolerance) return 'complete';
    if (percentage >= 80) return 'almost';
    if (percentage >= 50) return 'halfway';
    return 'starting';
  };

  // Format weight for display
  const formatWeight = (weight) => {
    return parseFloat(weight).toFixed(1);
  };

  return (
    <div className="scale-gauge">
      {/* Numeric Display */}
      <div className="gauge-numbers">
        <div className="current-weight">
          <span className="weight-value">{formatWeight(currentWeight)}</span>
          <span className="weight-unit">{unit}</span>
          {isStable && <span className="stable-indicator">STABLE</span>}
        </div>
        <div className="weight-separator">/</div>
        <div className="target-weight">
          <span className="weight-value">{formatWeight(targetWeight)}</span>
          <span className="weight-unit">{unit}</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="gauge-bar-container">
        <div
          className={`gauge-bar-fill ${getProgressColor()}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
        {/* Target line marker */}
        <div className="target-marker" style={{ left: '100%' }} />
        {/* Tolerance zone */}
        {tolerance > 0 && (
          <div
            className="tolerance-zone"
            style={{
              left: `${Math.max(0, (1 - tolerance / targetWeight) * 100)}%`,
              width: `${(tolerance / targetWeight) * 200}%`
            }}
          />
        )}
      </div>

      {/* Percentage */}
      <div className="gauge-percentage">
        <span className={`percentage-value ${getProgressColor()}`}>
          {percentage.toFixed(1)}%
        </span>
      </div>

      {/* Status Text */}
      <div className={`gauge-status ${getProgressColor()}`}>
        {isOverfilled ? (
          <>OVERFILLED by {formatWeight(currentWeight - targetWeight)} {unit}</>
        ) : isComplete ? (
          <>TARGET REACHED</>
        ) : (
          <>{formatWeight(remaining)} {unit} remaining</>
        )}
      </div>
    </div>
  );
}

export default ScaleGauge;
